import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck } from 'lucide-react';
import Button from '@components/Button/Button';
import evaluationsService from '@/services/evaluations/evaluationsService';
import EvaluationForm from './EvaluationForm';

interface Props {
    enrollmentId: string;
}

const statusPill = (status: string | undefined, label: (k: string) => string) => {
    if (status === 'SUBMITTED')
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700">{label('SUBMITTED')}</span>;
    if (status === 'DRAFT')
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700">{label('DRAFT')}</span>;
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500">{label('NOT_STARTED')}</span>;
};

const StudentEvaluationCard: React.FC<Props> = ({ enrollmentId }) => {
    const { t } = useTranslation('evaluation');
    const [open, setOpen] = useState(false);
    const [lastJudgeId, setLastJudgeId] = useState('');

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['evaluation', enrollmentId],
        queryFn: () => evaluationsService.getOne(enrollmentId),
        retry: false,
    });

    const noRubric = isError && (error as any)?.response?.status === 404;
    const judges = data?.judges ?? [];
    const started = judges.length > 0;
    const submittedCount = judges.filter((j) => j.status === 'SUBMITTED').length;
    // Same rule as the grid: any submission at all counts the student as "Submitted",
    // otherwise any draft scorecard counts as "Draft".
    const status = submittedCount > 0 ? 'SUBMITTED' : started ? 'DRAFT' : undefined;
    const lastUpdated = judges.reduce<(typeof judges)[number] | null>(
        (latest, j) => (!latest || j.updated_at > latest.updated_at ? j : latest),
        null,
    );

    return (
        <div className="bg-white rounded-xl shadow-sm border border-border p-6">
            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <ClipboardCheck size={18} className="text-blue-bg" />
                    {t('card.title')}
                </h3>
                {data && (
                    <Button variant={started ? 'outline' : 'primary'} size="sm" onClick={() => setOpen(true)}>
                        {started ? t('card.edit') : t('card.evaluate')}
                    </Button>
                )}
            </div>

            {isLoading && <p className="text-sm text-gray-400">…</p>}

            {noRubric && <p className="text-sm text-gray-500 italic">{t('card.noRubric')}</p>}

            {isError && !noRubric && <p className="text-sm text-red-500">{t('card.loadError')}</p>}

            {data && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div>
                        <p className="text-xs text-gray-500 mb-1">{t('grid.status')}</p>
                        {statusPill(status, (k) => t(`status.${k}`))}
                        {started && (
                            <p className="text-[10px] text-gray-400 mt-1">
                                {t('card.judgesCount', { submitted: submittedCount, total: judges.length })}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">{t('card.score')}</p>
                        <p className="text-lg font-extrabold text-gray-900">
                            {data.final.judges_counted > 0 ? data.final.total_weighted : '—'}
                            <span className="text-xs font-medium text-gray-400"> / 100</span>
                        </p>
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs text-gray-500 mb-1">
                            {t('card.rubric', { name: data.rubric.name, version: data.rubric.template_version })}
                        </p>
                        <p className="text-xs text-gray-500">
                            {lastUpdated
                                ? t('card.lastSaved', {
                                      when: new Date(lastUpdated.updated_at).toLocaleString(),
                                      who: lastUpdated.evaluator_name ?? '—',
                                  })
                                : t('card.notStarted')}
                        </p>
                    </div>
                </div>
            )}

            {open && (
                <EvaluationForm
                    enrollmentId={enrollmentId}
                    defaultJudgeId={lastJudgeId}
                    onJudgeChange={setLastJudgeId}
                    onClose={() => setOpen(false)}
                    onSaved={() => {
                        /* EvaluationForm invalidates ['evaluation', enrollmentId] — this card refetches. */
                    }}
                />
            )}
        </div>
    );
};

export default StudentEvaluationCard;
